import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { bankApi, apiErrorMessage } from '../services/api';
import { useOrg } from '../context/OrgContext';
import useExamHierarchy from '../hooks/useExamHierarchy';
import useExamDetail from '../hooks/useExamDetail';
import ExamPageHeader from '../components/exam/ExamPageHeader';
import QuestionBrowserModal from '../components/exam/QuestionBrowserModal';
import { Button, Card, PageSkeleton, ErrorState, EmptyState, Spinner } from '../components/ui';
import { getQuestionLabel, getQuestionText, getQuestionTags } from '../utils/question';

const PAGE_SIZE = 10;
// The org questions route caps a page at 1000 (default 200). Ask for the cap so
// the browser shows the whole pool of any realistic exam, and say so when it
// does not.
const QUESTION_LIMIT = 1000;

// Question-level experience for one exam: a topic navigator on the left
// (synced to the ?topic= query param so the exam library can deep-link here)
// and a searchable, paginated question list on the right.
export default function QuestionBankDetailPage() {
  const { examId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const topicId = searchParams.get('topic');
  const org = useOrg();
  const exam = useExamDetail(examId);
  const { sections, countsByTopic, totalQuestions, topicCount, loading, error } = useExamHierarchy(examId);

  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [questionsError, setQuestionsError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [viewerIndex, setViewerIndex] = useState(null);

  useEffect(() => {
    if (!org?.id) return undefined;
    let cancelled = false;
    setQuestionsLoading(true);
    setQuestionsError(null);
    setSearch('');
    setPage(0);
    setViewerIndex(null);
    bankApi.getQuestions(org.id, { examId, limit: QUESTION_LIMIT, ...(topicId ? { topicId } : {}) })
      .then((res) => { if (!cancelled) setQuestions(res.data || []); })
      .catch((err) => { if (!cancelled) setQuestionsError(apiErrorMessage(err, 'Failed to load questions')); })
      .finally(() => { if (!cancelled) setQuestionsLoading(false); });
    return () => { cancelled = true; };
  }, [examId, topicId, org?.id]);

  const activeTopic = useMemo(() => {
    if (!topicId) return null;
    for (const section of sections) {
      const topic = (section.topics || []).find((t) => t.id === topicId);
      if (topic) return { ...topic, sectionTitle: section.title };
    }
    return null;
  }, [sections, topicId]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return questions;
    return questions.filter((question) =>
      getQuestionText(question).toLowerCase().includes(query) ||
      getQuestionTags(question).join(' ').toLowerCase().includes(query)
    );
  }, [questions, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const visible = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const selectTopic = (id) => setSearchParams(id ? { topic: id } : {});

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorState message={error} />;

  const title = exam?.title || exam?.name || 'Question bank';
  const activeTopicName = activeTopic ? (activeTopic.title || activeTopic.name) : 'All questions';

  return (
    <>
      <ExamPageHeader
        examId={examId}
        active="bank"
        title={title}
        description={exam?.description || 'Browse, search and inspect every question in this exam pool.'}
        stats={{ sections: sections.length, topics: topicCount, questions: totalQuestions }}
      />
      <div className="bank-layout">
        <Card className="bank-sidebar">
          <p className="eyebrow">Topics</p>
          <nav className="topic-nav">
            <button className={`topic-nav-item ${!topicId ? 'active' : ''}`} onClick={() => selectTopic(null)}>
              <span>All questions</span>
              <small>{totalQuestions}</small>
            </button>
            {sections.map((section) => (
              <div key={section.id} className="topic-nav-group">
                <span className="topic-nav-label">{section.title}</span>
                {(section.topics || []).map((topic) => (
                  <button
                    key={topic.id}
                    className={`topic-nav-item ${topicId === topic.id ? 'active' : ''}`}
                    onClick={() => selectTopic(topic.id)}
                  >
                    <span>{topic.title || topic.name}</span>
                    <small>{countsByTopic[topic.id] || 0}</small>
                  </button>
                ))}
              </div>
            ))}
          </nav>
        </Card>
        <Card className="bank-content">
          <div className="bank-content-head">
            <div>
              <h2>{activeTopicName}</h2>
              <p className="muted">
                {activeTopic ? `${activeTopic.sectionTitle} · ` : ''}
                {questionsLoading
                  ? 'Loading…'
                  : `${filtered.length} ${filtered.length === 1 ? 'question' : 'questions'}${search ? ' matching your search' : ''}${questions.length >= QUESTION_LIMIT ? ` (first ${QUESTION_LIMIT} loaded)` : ''}`}
              </p>
            </div>
            <div className="bank-search">
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search question text or tags"
                aria-label="Search questions"
              />
            </div>
          </div>
          {questionsLoading ? (
            <div className="page-loading"><Spinner /> Loading questions…</div>
          ) : questionsError ? (
            <ErrorState message={questionsError} />
          ) : !filtered.length ? (
            <EmptyState title={search ? 'No matching questions' : 'No questions yet'}>
              {search ? 'Try a different search term or clear the search.' : 'This topic has no questions in the pool yet.'}
            </EmptyState>
          ) : (
            <>
              <div className="question-rows">
                {visible.map((question, index) => {
                  const globalIndex = currentPage * PAGE_SIZE + index;
                  const tags = getQuestionTags(question);
                  return (
                    <button
                      key={question.id || globalIndex}
                      className="question-row"
                      onClick={() => setViewerIndex(globalIndex)}
                    >
                      <span className="question-row-num">{globalIndex + 1}</span>
                      <span className="question-row-main">
                        <span className="question-row-text">{getQuestionLabel(question, globalIndex)}</span>
                        {tags.length > 0 && (
                          <span className="question-tags">
                            {tags.slice(0, 4).map((tag) => <span key={tag} className="question-tag">{tag}</span>)}
                          </span>
                        )}
                      </span>
                      <span className="question-row-open">View →</span>
                    </button>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <div className="question-pagination">
                  <Button variant="secondary" onClick={() => setPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0}>
                    ← Prev
                  </Button>
                  <span>Page {currentPage + 1} / {totalPages}</span>
                  <Button variant="secondary" onClick={() => setPage(Math.min(totalPages - 1, currentPage + 1))} disabled={currentPage >= totalPages - 1}>
                    Next →
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
      <QuestionBrowserModal
        open={viewerIndex != null}
        onClose={() => setViewerIndex(null)}
        title={activeTopicName}
        questions={filtered}
        index={viewerIndex}
        onNavigate={(next) => setViewerIndex(Math.min(Math.max(next, 0), filtered.length - 1))}
      />
    </>
  );
}
